import { Routes } from '@angular/router';
import { MsalGuard, MsalRedirectComponent } from '@azure/msal-angular';
import { HomeComponent } from './modules/home/home.component';
import { ErrorComponent } from './modules/error/error.component';
import { UsersViewContainerComponent } from './modules/users/view-container/user-view-container.component';
import { RolesGuard } from './shared/guards/roles.guard';
import { EnergyCreditsViewContainerComponent } from './modules/energy-credits/view-container/energy-credits-view-container.component';
import { ELearnComponent } from './modules/e-learn/e-learn.component';
import { permissionConstants } from './shared/services';
import { cacheResolver } from './shared/resolvers/cache.resolver';
import { CsvFileUploadComponent } from './modules/qbuilt/csv-file-upload/csv-file-upload.component';
import { ChecklistTableComponent } from './modules/qbuilt/checklist-table/checklist-table.component';
import { NewEditChecklistComponent } from './modules/qbuilt/new-edit-checklist/new-edit-checklist.component';
import { PendingChangesGuard } from './shared/guards/pending-changes.guard';
import { FacilityConfigComponent } from './modules/facility-config/facility-config/facility-config.component';
import { NewEditFacilityComponent } from './modules/facility-config/new-edit-facility/new-edit-facility.component';
import { QbuiltLandingPageComponent } from './qbuilt-inspector/qbuilt-landing-page/qbuilt-landing-page.component';
import { ActiveInspectionComponent } from './qbuilt-inspector/active-inspection/active-inspection.component';
import { SupportContainerComponent } from './modules/support/support-container/support-container.component';
import { EnergyCreditsAdminFacilitiesComponent } from './modules/support/energy-credits-admin-facilities/energy-credits-admin-facilities.component';
import { TaskManagementComponent } from './modules/corp-admin/task-management/task-management.component';
import { NewEditTaskComponent } from './modules/corp-admin/task-management/new-edit-task-management/new-edit-task-management.component';
import { UserAdminContainerComponent } from './modules/qbuilt/user-admin-container/user-admin-container.component';
import { UserEditComponent } from './modules/qbuilt/user-edit/user-edit.component';
import { UnsavedChangesGuard } from './shared/guards/unsaved-changes.guard';

export const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    canActivate: [MsalGuard],
    children: [
      {
        path: 'administration/users',
        component: UsersViewContainerComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          expectedRoles: permissionConstants.siteAdmin,
        },
      },
      {
        path: 'support',
        component: SupportContainerComponent,
        canActivate: [MsalGuard, RolesGuard],
        data: {
          expectedRoles: permissionConstants.siteAdmin,
        },
      },
      {
        path: 'support/energy-credits-facilities',
        component: EnergyCreditsAdminFacilitiesComponent,
        canActivate: [MsalGuard, RolesGuard],
        data: {
          expectedRoles: ['EnergyCreditsAdmin'].concat(
            permissionConstants.siteAdmin,
          ),
        },
      },
      {
        path: 'administration/csv-file-upload',
        component: CsvFileUploadComponent,
        canActivate: [MsalGuard, RolesGuard],
        data: {
          expectedRoles: permissionConstants.homeOfficeAdmin,
        },
      },
      {
        path: 'energy-credits',
        component: EnergyCreditsViewContainerComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [PendingChangesGuard],
        data: {
          expectedRoles: permissionConstants.energyCredits.concat(
            permissionConstants.siteAdmin,
          ),
        },
        resolve: {
          // The isReady property isn't needed by the component, but
          // it's a way to force the resolver to populate before it renders
          isReady: cacheResolver,
        },
      },
      {
        path: 'cqms',
        component: ChecklistTableComponent,
        canActivate: [MsalGuard, RolesGuard],
        data: {
          expectedRoles: permissionConstants.qBuilt,
        },
        resolve: {
          isReady: cacheResolver,
        },
      },
      {
        path: 'cqms/new-checklist',
        component: NewEditChecklistComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          expectedRoles: permissionConstants.qBuilt,
        },
        resolve: {
          isReady: cacheResolver,
        },
      },
      {
        path: 'cqms/edit-checklist/:id',
        component: NewEditChecklistComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          expectedRoles: permissionConstants.qBuilt,
        },
        resolve: {
          isReady: cacheResolver,
        },
      },
      {
        path: 'cqms/users',
        component: UserAdminContainerComponent,
        canActivate: [MsalGuard, RolesGuard],
        data: {
          expectedRoles: permissionConstants.qBuilt,
        },
        resolve: {
          isReady: cacheResolver,
        },
      },
      {
        path: 'cqms/edit-user/:userId',
        component: UserEditComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          expectedRoles: permissionConstants.qBuilt,
        },
        resolve: {
          isReady: cacheResolver,
        },
      },
      {
        path: 'facility-configuration',
        component: FacilityConfigComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [PendingChangesGuard],
        data: {
          expectedRoles: permissionConstants.qBuilt,
        },

        resolve: {
          isReady: cacheResolver,
        },
      },
      {
        path: 'facility-configuration/new',
        component: NewEditFacilityComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          expectedRoles: permissionConstants.qBuilt,
        },
      },
      {
        path: 'facility-configuration/edit/:id',
        component: NewEditFacilityComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          expectedRoles: permissionConstants.qBuilt,
        },
      },
      {
        path: 'corp-admin/task-management',
        component: TaskManagementComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [PendingChangesGuard],
        data: {
          expectedRoles: permissionConstants.homeOfficeAdmin.concat(
            permissionConstants.siteAdmin,
          ),
        },
      },
      {
        path: 'corp-admin/task-management/new-task',
        component: NewEditTaskComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          expectedRoles: permissionConstants.homeOfficeAdmin.concat(
            permissionConstants.siteAdmin,
          ),
        },
      },
      {
        path: 'corp-admin/task-management/edit-task/:id',
        component: NewEditTaskComponent,
        canActivate: [MsalGuard, RolesGuard],
        canDeactivate: [UnsavedChangesGuard],
        data: {
          expectedRoles: permissionConstants.homeOfficeAdmin.concat(
            permissionConstants.siteAdmin,
          ),
        },
      },
      // NOTE: Commenting out until e-learn has content
      // {
      //   path: 'e-learn',
      //   component: ELearnComponent,
      //   canActivate: [MsalGuard],
      // },
      {
        path: 'auth',
        component: MsalRedirectComponent,
      },
    ],
  },
  {
    path: 'cqms-home',
    component: QbuiltLandingPageComponent,
    canActivate: [MsalGuard, RolesGuard],
    data: {
      expectedRoles: permissionConstants.qBuiltInspector.concat(
        permissionConstants.siteAdmin,
      ),
    },
    resolve: {
      isReady: cacheResolver,
    },
  },
  {
    path: 'cqms-home/inspections/:zone',
    component: ActiveInspectionComponent,
    canActivate: [MsalGuard, RolesGuard],
    data: {
      expectedRoles: permissionConstants.qBuiltInspector,
    },
  },
  {
    path: 'authorization-error',
    component: ErrorComponent,
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];
